import { motion } from 'framer-motion';
import { useAnimatedNumber } from '../../../hooks/animations/useAnimatedNumber';
import type { TrustScoreRingProps } from '../types';

const sizeConfig = {
  sm: { width: 48, strokeWidth: 4, fontSize: 'text-xs' },
  md: { width: 64, strokeWidth: 5, fontSize: 'text-sm' },
  lg: { width: 96, strokeWidth: 6, fontSize: 'text-lg' },
  xl: { width: 144, strokeWidth: 8, fontSize: 'text-2xl' },
};

const getScoreColor = (score: number): string => {
  if (score >= 80) return '#10b981'; // success
  if (score >= 60) return '#3b6fff'; // primary
  if (score >= 40) return '#f59e0b'; // warning
  return '#f43f5e'; // danger
};

const getScoreLabel = (score: number): string => {
  if (score >= 80) return 'Elite';
  if (score >= 60) return 'Trusted';
  if (score >= 40) return 'Rising';
  return 'New';
};

export function TrustScoreRing({
  score,
  size = 'md',
  animated = true,
  interactive = false,
  showLabel = true,
  onClick,
}: TrustScoreRingProps): JSX.Element {
  const { width, strokeWidth, fontSize } = sizeConfig[size];
  const radius = (width - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  
  const animatedScore = useAnimatedNumber(animated ? score : 0, 1500);
  const displayScore = animated ? animatedScore : score;
  const offset = circumference - (displayScore / 100) * circumference;
  const color = getScoreColor(score);

  return (
    <motion.div
      className={`relative inline-flex items-center justify-center ${
        interactive ? 'cursor-pointer' : ''
      }`}
      onClick={onClick}
      whileHover={interactive ? { scale: 1.05 } : undefined}
      whileTap={interactive ? { scale: 0.95 } : undefined}
    >
      <svg
        width={width}
        height={width}
        viewBox={`0 0 ${width} ${width}`}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={width / 2}
          cy={width / 2}
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={strokeWidth}
        />
        
        {/* Progress circle */}
        <motion.circle
          cx={width / 2}
          cy={width / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.5, ease: 'easeOut', delay: 0.2 }}
          style={{
            filter: `drop-shadow(0 0 ${strokeWidth / 2}px ${color}40)`,
          }}
        />
      </svg>
      
      {/* Score text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`${fontSize} font-bold text-dark-900`}>
          {displayScore}
        </span>
        {showLabel && size !== 'sm' && (
          <span className="text-[8px] uppercase tracking-wider text-dark-500 font-medium">
            {getScoreLabel(score)}
          </span>
        )}
      </div>
      
      {/* Glow effect for high scores */}
      {score >= 80 && (
        <motion.div
          className="absolute inset-0 rounded-full"
          animate={{
            boxShadow: [
              `0 0 ${strokeWidth}px ${color}20`,
              `0 0 ${strokeWidth * 2}px ${color}40`,
              `0 0 ${strokeWidth}px ${color}20`,
            ],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      )}
    </motion.div>
  );
}
